import { DEFAULT_FONT_FAMILY, type Fill, type Primitive, type Stroke } from '@vp/shared-types';
import type { Rect } from '@vp/core';
import type { PDFDocument, PDFFont, PDFImage, PDFName, PDFOperator, PDFPage } from 'pdf-lib';
import { parseColor } from './color';
import { collectExportItems, type ExportOptions } from './export';
import { arrowHead, dashPattern, trimForArrows } from './pathgeom';
import { patternSpacing, patternThickness } from './canvas2d';
import type { RenderItem, Scene } from './scene';
import { layoutText } from './text';
import { orientTris, roundStroke, strokeVariable } from './webgl/tessellate';

/**
 * PDF export (§13.1): a real vector document — shapes and lines as paths, text as text in the
 * embedded fonts (subset, so the file stays small; selectable and searchable, any script the fonts
 * have), pictures as images, frames clipped. One page per area: the whole export, or each frame.
 * pdf-lib and fontkit are loaded only when a PDF is made.
 */

/** A font file and the characters it has (bundled fonts come split into Latin and Cyrillic files). */
export interface PdfFontFace {
  bytes: Uint8Array;
  covers(codePoint: number): boolean;
}

export interface PdfExportOptions {
  /** Font files for a CSS family stack and style, best first; null/empty = the default font is used. */
  fontFaces?: (family: string, bold: boolean, italic: boolean) => Promise<PdfFontFace[]>;
  title?: string;
}

/** PDF pages cannot be larger than 200 inches on a side (14 400 pt). */
export const PDF_MAX_PT = 14400;

/** Page size in points for a world area: 1 world unit = `scale` CSS px, 1 px = 0.75 pt, scaled down to the PDF limit. */
export function pageSize(w: number, h: number, scale = 1): { ptW: number; ptH: number; k: number } {
  const s = scale > 0 && Number.isFinite(scale) ? scale * 0.75 : 0.75;
  const k = s * Math.min(1, PDF_MAX_PT / Math.max(w * s, h * s, 1));
  return { ptW: Math.max(1, w * k), ptH: Math.max(1, h * k), k };
}

type Lib = typeof import('pdf-lib');

export async function exportPdf(scene: Scene, pages: ExportOptions[], opts: PdfExportOptions = {}): Promise<Uint8Array> {
  const lib: Lib = await import('pdf-lib');
  const fontkit = (await import('@pdf-lib/fontkit')).default;
  const doc = await lib.PDFDocument.create();
  doc.registerFontkit(fontkit);
  doc.setTitle(opts.title ?? '');
  doc.setProducer('Visual Platform');
  doc.setCreator('Visual Platform');
  const w = new PdfWriter(lib, doc, opts);
  let count = 0;
  for (const p of pages) {
    const col = collectExportItems(scene, p);
    if (!col) continue;
    await w.page(col.items, col.bounds, p);
    count++;
  }
  if (!count) throw new Error('Nothing to export');
  return doc.save({ useObjectStreams: true });
}

class PdfWriter {
  private faces = new Map<string, Promise<PdfFontFace[]>>();
  private embedded = new Map<PdfFontFace, Promise<PDFFont | null>>();
  private fallback: Promise<PDFFont> | null = null;
  private images = new Map<string, Promise<PDFImage | null>>();
  private pg!: PDFPage;
  private ops: PDFOperator[] = [];
  private gs = new Map<string, PDFName>();

  constructor(
    private lib: Lib,
    private doc: PDFDocument,
    private opts: PdfExportOptions,
  ) {}

  async page(items: RenderItem[], bounds: Rect, eo: ExportOptions): Promise<void> {
    const L = this.lib;
    const { ptW, ptH, k } = pageSize(bounds.w, bounds.h, eo.scale);
    this.pg = this.doc.addPage([ptW, ptH]);
    this.gs.clear();
    this.ops = [];
    // world (y down) → page (y up)
    this.ops.push(L.pushGraphicsState(), L.concatTransformationMatrix(k, 0, 0, -k, -bounds.x * k, ptH + bounds.y * k));
    if (eo.background) {
      this.fillColor(eo.background, 1);
      this.ops.push(L.rectangle(bounds.x, bounds.y, bounds.w, bounds.h), L.fill());
    }
    for (const it of items) {
      const o = it.obj;
      this.ops.push(L.pushGraphicsState());
      if (it.clip) this.ops.push(L.rectangle(it.clip.x, it.clip.y, it.clip.w, it.clip.h), L.clip(), L.endPath());
      const c = Math.cos(o.rotation);
      const s = Math.sin(o.rotation);
      const cx = o.x + o.w / 2;
      const cy = o.y + o.h / 2;
      this.ops.push(L.concatTransformationMatrix(c, s, -s, c, cx - (c * o.w) / 2 + (s * o.h) / 2, cy - (s * o.w) / 2 - (c * o.h) / 2));
      for (const p of it.prims) await this.primitive(p, (o.style.opacity ?? 1) * (p.opacity ?? 1), eo);
      this.ops.push(L.popGraphicsState());
    }
    this.ops.push(L.popGraphicsState());
    this.pg.pushOperators(...this.ops);
  }

  // ---------- state ----------

  /** Transparency for fill / stroke (ExtGState, one per value). */
  private alpha(fill: number, stroke: number, blend?: 'Darken'): void {
    const key = `${fill.toFixed(3)}/${stroke.toFixed(3)}/${blend ?? ''}`;
    let name = this.gs.get(key);
    if (!name) {
      name = this.pg.node.newExtGState(
        'GS',
        this.doc.context.obj(
          blend ? { Type: 'ExtGState', ca: fill, CA: stroke, BM: this.lib.PDFName.of(blend) } : { Type: 'ExtGState', ca: fill, CA: stroke },
        ),
      );
      this.gs.set(key, name);
    }
    this.ops.push(this.lib.setGraphicsState(name));
  }

  private fillColor(css: string, opacity: number): number {
    const [r, g, b, a] = parseColor(css);
    this.ops.push(this.lib.setFillingRgbColor(r, g, b));
    return a * opacity;
  }

  private strokeStyle(st: Stroke, opacity: number): number {
    const L = this.lib;
    const [r, g, b, a] = parseColor(st.color);
    const dash = dashPattern(st.dash, st.width);
    this.ops.push(L.setStrokingRgbColor(r, g, b), L.setLineWidth(st.width), L.setDashPattern(dash, 0), L.setLineCap(st.dash === 'dotted' ? L.LineCapStyle.Round : L.LineCapStyle.Butt), L.setLineJoin(L.LineJoinStyle.Miter));
    return a * opacity;
  }

  // ---------- geometry ----------

  /** A triangulated ribbon as one nonzero fill (painted once, also when see-through). */
  private fillTris(raw: number[]): void {
    const L = this.lib;
    const tris = orientTris(raw);
    if (tris.length < 6) return;
    for (let i = 0; i < tris.length; i += 6) {
      this.ops.push(L.moveTo(tris[i], tris[i + 1]), L.lineTo(tris[i + 2], tris[i + 3]), L.lineTo(tris[i + 4], tris[i + 5]), L.closePath());
    }
    this.ops.push(L.fill());
  }

  private poly(pts: number[], close: boolean): void {
    const L = this.lib;
    this.ops.push(L.moveTo(pts[0], pts[1]));
    for (let i = 2; i < pts.length; i += 2) this.ops.push(L.lineTo(pts[i], pts[i + 1]));
    if (close) this.ops.push(L.closePath());
  }

  private ellipse(x: number, y: number, w: number, h: number): void {
    const L = this.lib;
    const K = 0.5522847498;
    const rx = w / 2;
    const ry = h / 2;
    const cx = x + rx;
    const cy = y + ry;
    this.ops.push(
      L.moveTo(cx + rx, cy),
      L.appendBezierCurve(cx + rx, cy + ry * K, cx + rx * K, cy + ry, cx, cy + ry),
      L.appendBezierCurve(cx - rx * K, cy + ry, cx - rx, cy + ry * K, cx - rx, cy),
      L.appendBezierCurve(cx - rx, cy - ry * K, cx - rx * K, cy - ry, cx, cy - ry),
      L.appendBezierCurve(cx + rx * K, cy - ry, cx + rx, cy - ry * K, cx + rx, cy),
      L.closePath(),
    );
  }

  private roundRect(x: number, y: number, w: number, h: number, r: number): void {
    const L = this.lib;
    if (r <= 0) {
      this.ops.push(L.rectangle(x, y, w, h));
      return;
    }
    const K = 0.5522847498 * r;
    this.ops.push(
      L.moveTo(x + r, y),
      L.lineTo(x + w - r, y),
      L.appendBezierCurve(x + w - r + K, y, x + w, y + r - K, x + w, y + r),
      L.lineTo(x + w, y + h - r),
      L.appendBezierCurve(x + w, y + h - r + K, x + w - r + K, y + h, x + w - r, y + h),
      L.lineTo(x + r, y + h),
      L.appendBezierCurve(x + r - K, y + h, x, y + h - r + K, x, y + h - r),
      L.lineTo(x, y + r),
      L.appendBezierCurve(x, y + r - K, x + r - K, y, x + r, y),
      L.closePath(),
    );
  }

  /** Fill and stroke the outline built by `outline` (called again for each pass that needs it). */
  private paint(outline: () => void, box: Rect, fill: Fill, stroke: Stroke | null, opacity: number, evenOdd = false): void {
    const L = this.lib;
    if (fill.kind === 'solid') {
      this.ops.push(L.pushGraphicsState());
      this.alpha(this.fillColor(fill.color, opacity), 1);
      outline();
      this.ops.push(evenOdd ? L.PDFOperator.of(L.PDFOperatorNames.FillEvenOdd) : L.fill(), L.popGraphicsState());
    } else if (fill.kind === 'lines' || fill.kind === 'grid') {
      // the pattern: lines across the outline's box, clipped to the outline
      this.ops.push(L.pushGraphicsState());
      outline();
      this.ops.push(evenOdd ? L.clipEvenOdd() : L.clip(), L.endPath());
      if (fill.background) {
        this.alpha(this.fillColor(fill.background, opacity), 1);
        this.ops.push(L.rectangle(box.x, box.y, box.w, box.h), L.fill());
      }
      const sp = patternSpacing(fill);
      const [r, g, b, a] = parseColor(fill.color);
      this.ops.push(L.setStrokingRgbColor(r, g, b), L.setLineWidth(patternThickness(sp)), L.setDashPattern([], 0));
      this.alpha(1, a * opacity);
      const cx = box.x + box.w / 2;
      const cy = box.y + box.h / 2;
      const R = Math.hypot(box.w, box.h) / 2 + sp;
      const angles = fill.kind === 'lines' ? [fill.angle ?? Math.PI / 4] : [0, Math.PI / 2];
      for (const ang of angles) {
        // lines along the direction perpendicular to `ang`, as in the canvas pattern (vertical lines rotated by the angle)
        const dx = Math.cos(ang);
        const dy = Math.sin(ang);
        for (let t = -R; t <= R; t += sp) {
          const px = cx + dx * t;
          const py = cy + dy * t;
          this.ops.push(L.moveTo(px - dy * R, py + dx * R), L.lineTo(px + dy * R, py - dx * R));
        }
        this.ops.push(L.stroke());
      }
      this.ops.push(L.popGraphicsState());
    }
    if (stroke && stroke.width > 0) {
      this.ops.push(L.pushGraphicsState());
      this.alpha(1, this.strokeStyle(stroke, opacity));
      outline();
      this.ops.push(L.stroke(), L.popGraphicsState());
    }
  }

  // ---------- primitives ----------

  private async primitive(p: Primitive | { k: 'raster'; x: number; y: number; w: number; h: number; bitmap: ImageBitmap | null }, opacity: number, eo: ExportOptions): Promise<void> {
    switch (p.k) {
      case 'shape': {
        const box = { x: p.x, y: p.y, w: p.w, h: p.h };
        const outline =
          p.shape === 'rect'
            ? () => this.roundRect(p.x, p.y, p.w, p.h, Math.min(p.radius ?? 0, p.w / 2, p.h / 2))
            : p.shape === 'ellipse'
              ? () => this.ellipse(p.x, p.y, p.w, p.h)
              : p.shape === 'triangle'
                ? () => this.poly([p.x + p.w / 2, p.y, p.x + p.w, p.y + p.h, p.x, p.y + p.h], true)
                : () => this.poly([p.x + p.w / 2, p.y, p.x + p.w, p.y + p.h / 2, p.x + p.w / 2, p.y + p.h, p.x, p.y + p.h / 2], true);
        this.paint(outline, box, p.fill, p.stroke, opacity);
        return;
      }
      case 'path': {
        const box = ringBox(p.points);
        if (p.closed) {
          const rings = [p.points, ...(p.holes ?? [])];
          this.paint(() => rings.forEach((r) => this.poly(r, true)), box, p.fill, p.stroke, opacity, !!p.holes?.length);
          return;
        }
        if (!p.stroke || p.stroke.width <= 0) return;
        const L = this.lib;
        const sw = p.stroke.width;
        const pts = trimForArrows(p.points, !!p.arrowStart, !!p.arrowEnd, sw);
        const highlight = p.blend === 'highlight' ? 'Darken' : undefined;
        this.ops.push(L.pushGraphicsState());
        if (p.widths && p.widths.length === p.points.length / 2) {
          const tris: number[] = [];
          strokeVariable(p.points, p.widths, false, tris);
          if (p.round) roundStroke(p.points, p.widths, tris);
          this.alpha(this.fillColor(p.stroke.color, opacity), 1, highlight);
          this.fillTris(tris);
        } else {
          this.alpha(1, this.strokeStyle(p.stroke, opacity), highlight);
          if (p.round) this.ops.push(L.setLineCap(L.LineCapStyle.Round), L.setLineJoin(L.LineJoinStyle.Round));
          this.poly(pts, false);
          this.ops.push(L.stroke());
        }
        this.ops.push(L.popGraphicsState());
        for (const atEnd of [false, true]) {
          if (!(atEnd ? p.arrowEnd : p.arrowStart)) continue;
          const h = arrowHead(p.points, atEnd, sw);
          if (!h) continue;
          this.ops.push(L.pushGraphicsState());
          this.alpha(this.fillColor(p.stroke.color, opacity), 1);
          this.poly(h.tri, true);
          this.ops.push(L.fill(), L.popGraphicsState());
        }
        return;
      }
      case 'text':
        await this.text(p, opacity);
        return;
      case 'image': {
        const img = await this.image('att:' + p.src, async () => {
          const url = eo.imageDataUrl ? await eo.imageDataUrl(p.src) : null;
          return url ? dataUrlBytes(url) : null;
        });
        if (img) this.drawImage(img, p.x, p.y, p.w, p.h, opacity);
        else this.paint(() => this.ops.push(this.lib.rectangle(p.x, p.y, p.w, p.h)), p, { kind: 'solid', color: '#e5e7eb' }, null, opacity);
        return;
      }
      case 'raster': {
        const bmp = p.bitmap;
        if (!bmp) return;
        const img = await this.image('bmp:' + Math.random(), async () => ({ bytes: await bitmapPng(bmp), type: 'png' }));
        if (img) this.drawImage(img, p.x, p.y, p.w, p.h, opacity);
      }
    }
  }

  private drawImage(img: PDFImage, x: number, y: number, w: number, h: number, opacity: number): void {
    const L = this.lib;
    const name = this.pg.node.newXObject('Image', img.ref);
    this.ops.push(L.pushGraphicsState());
    this.alpha(opacity, opacity);
    // the image space is a unit square with y up: flip it back into the y-down world
    this.ops.push(L.concatTransformationMatrix(w, 0, 0, -h, x, y + h), L.drawObject(name), L.popGraphicsState());
  }

  private image(key: string, load: () => Promise<{ bytes: Uint8Array; type: string } | null>): Promise<PDFImage | null> {
    let p = this.images.get(key);
    if (!p) {
      p = (async () => {
        try {
          const src = await load();
          if (!src) return null;
          if (src.type === 'png') return await this.doc.embedPng(src.bytes);
          if (src.type === 'jpeg') return await this.doc.embedJpg(src.bytes);
          // WebP, GIF, SVG…: through a canvas into PNG
          return await this.doc.embedPng(await toPng(src.bytes, src.type));
        } catch {
          return null;
        }
      })();
      this.images.set(key, p);
    }
    return p;
  }

  // ---------- text ----------

  private facesOf(family: string, bold: boolean, italic: boolean): Promise<PdfFontFace[]> {
    const key = `${family}|${bold}|${italic}`;
    let p = this.faces.get(key);
    if (!p) {
      p = (async () => {
        const own = (await this.opts.fontFaces?.(family, bold, italic).catch(() => [])) ?? [];
        // characters the family lacks come from the default font
        const dflt = family === DEFAULT_FONT_FAMILY ? [] : ((await this.opts.fontFaces?.(DEFAULT_FONT_FAMILY, bold, italic).catch(() => [])) ?? []);
        return [...own, ...dflt];
      })();
      this.faces.set(key, p);
    }
    return p;
  }

  private font(face: PdfFontFace): Promise<PDFFont | null> {
    let p = this.embedded.get(face);
    if (!p) {
      p = this.doc.embedFont(face.bytes, { subset: true }).catch(() => null);
      this.embedded.set(face, p);
    }
    return p;
  }

  /** Helvetica, for Latin-1 text when no font file is available at all. */
  private standard(): Promise<PDFFont> {
    return (this.fallback ??= this.doc.embedFont(this.lib.StandardFonts.Helvetica));
  }

  private async text(p: Extract<Primitive, { k: 'text' }>, opacity: number): Promise<void> {
    const L = this.lib;
    const st = p.style;
    const layout = layoutText(p.text, st, p.w, p.h);
    const faces = await this.facesOf(st.fontFamily ?? DEFAULT_FONT_FAMILY, !!st.bold, !!st.italic);
    const [r, g, b, a] = parseColor(st.color);
    let started = false;
    for (const line of layout.lines) {
      if (!line.text) continue;
      if (!started) {
        this.ops.push(L.pushGraphicsState(), L.setFillingRgbColor(r, g, b));
        this.alpha(a * opacity, a * opacity);
        started = true;
      }
      let x = p.x + line.x;
      const y = p.y + line.y;
      for (const run of await this.runs(line.text, faces)) {
        if (!run.font) {
          x += run.width(st.fontSize);
          continue;
        }
        const name = this.pg.node.newFontDictionary(run.font.name, run.font.ref);
        // text space: y up again, at the baseline
        this.ops.push(L.beginText(), L.setFontAndSize(name, st.fontSize), L.setTextMatrix(1, 0, 0, -1, x, y), L.showText(run.font.encodeText(run.text)), L.endText());
        x += run.font.widthOfTextAtSize(run.text, st.fontSize);
      }
    }
    if (started) this.ops.push(L.popGraphicsState());
  }

  /** The line cut into pieces each drawn with the first font that has its characters. */
  private async runs(text: string, faces: PdfFontFace[]): Promise<{ text: string; font: PDFFont | null; width: (size: number) => number }[]> {
    const out: { text: string; font: PDFFont | null; width: (size: number) => number }[] = [];
    let cur: { text: string; face: PdfFontFace | null } | null = null;
    const flush = async () => {
      if (!cur || !cur.text) return;
      const font = cur.face ? await this.font(cur.face) : null;
      if (font) out.push({ text: cur.text, font, width: (s) => font.widthOfTextAtSize(cur!.text, s) });
      else {
        // no font file: Latin-1 in Helvetica; anything else is left as a gap of about its width
        const std = await this.standard();
        const t = cur.text;
        const latin = [...t].every((ch) => ch.codePointAt(0)! < 256);
        out.push(latin ? { text: t, font: std, width: (s) => std.widthOfTextAtSize(t, s) } : { text: t, font: null, width: (s) => t.length * s * 0.55 });
      }
    };
    for (const ch of text) {
      const cp = ch.codePointAt(0)!;
      // spaces join the current piece
      const face: PdfFontFace | null = cp === 32 && cur ? cur.face : (faces.find((f) => f.covers(cp)) ?? null);
      if (cur && cur.face === face) cur.text += ch;
      else {
        await flush();
        cur = { text: ch, face };
      }
    }
    await flush();
    return out;
  }
}

function ringBox(pts: number[]): Rect {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i < pts.length; i += 2) {
    x0 = Math.min(x0, pts[i]);
    x1 = Math.max(x1, pts[i]);
    y0 = Math.min(y0, pts[i + 1]);
    y1 = Math.max(y1, pts[i + 1]);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

function dataUrlBytes(url: string): { bytes: Uint8Array; type: string } | null {
  const m = /^data:([^;,]+)?(;base64)?,(.*)$/s.exec(url);
  if (!m) return null;
  const mime = (m[1] ?? '').toLowerCase();
  const bin = m[2] ? atob(m[3]) : decodeURIComponent(m[3]);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const type = mime.includes('png') ? 'png' : mime.includes('jpeg') || mime.includes('jpg') ? 'jpeg' : mime;
  return { bytes, type };
}

async function toPng(bytes: Uint8Array, mime: string): Promise<Uint8Array> {
  const bmp = await createImageBitmap(new Blob([bytes as BlobPart], { type: mime }));
  try {
    return await bitmapPng(bmp);
  } finally {
    bmp.close();
  }
}

async function bitmapPng(bmp: ImageBitmap): Promise<Uint8Array> {
  const c = new OffscreenCanvas(bmp.width, bmp.height);
  c.getContext('2d')!.drawImage(bmp, 0, 0);
  return new Uint8Array(await (await c.convertToBlob({ type: 'image/png' })).arrayBuffer());
}
